import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { findLastIndex, isEqual } from 'lodash';

import { usePrevious } from '../../../../shared/hooks/hooks';

import {
  getNormalizedProductType,
  isCanonicalProduct
} from '../../../../shared/utils/product/product';

import {
  ACCESSORY_FILTER_KEYS,
  TIRE_FILTER_KEYS,
  WHEEL_FILTER_KEYS
} from '../../../../shared/constants/filters';
import { PRODUCT_TYPES } from '../../../../shared/constants/products';

function getSortedNextFilters(nextFilters, filterKeys) {
  let sortedNextFilters = null;

  if (Array.isArray(nextFilters) && Array.isArray(filterKeys)) {
    sortedNextFilters = nextFilters.sort((a, b) => {
      const aKeyIdx = filterKeys.indexOf(a?.key);
      const bKeyIdx = filterKeys.indexOf(b?.key);

      return aKeyIdx - bKeyIdx;
    });
  }

  return sortedNextFilters;
}

function sortFilterOptions(a, b) {
  if (!isNaN(a) && !isNaN(b)) {
    return a - b;
  } else {
    let comp = 0;
    if (a < b) comp = 1;
    if (b > a) comp = -1;

    return comp;
  }
}

function useCanonicalForm(product, formType = PRODUCT_TYPES.WHEELS) {
  const { aggregates } = product || {};
  const { nextFilters } = aggregates || {};
  const prevNextFilters = usePrevious(nextFilters);
  const multipleNextFilterOptions =
    nextFilters?.filter(nextFilter => nextFilter.options.length === 1).length >
    1;
  const isCanonical = isCanonicalProduct(product);
  const productType = getNormalizedProductType(product);
  const [canonicalFilters, setCanonicalFilters] = useState({});
  const [selectedCanonicalFilters, setSelectedCanonicalFilters] = useState({});
  const [selectedQueryFilters, setSelectedQueryFilters] = useState({});
  const [shouldContinueAutoSelecting, setShouldContinueAutoSelecting] =
    useState(!multipleNextFilterOptions);
  const canonicalFiltersRef = useRef(canonicalFilters);
  const selectedCanonicalFiltersRef = useRef(selectedCanonicalFilters);
  const selectedQueryFiltersRef = useRef(selectedQueryFilters);
  const lastSelectedFilterKeyRef = useRef(null);
  const nextFiltersRef = useRef(null);

  canonicalFiltersRef.current = canonicalFilters;
  selectedCanonicalFiltersRef.current = selectedCanonicalFilters;
  selectedQueryFiltersRef.current = selectedQueryFilters;

  let selectedFilterKeys = TIRE_FILTER_KEYS;
  if (formType === PRODUCT_TYPES.WHEELS) selectedFilterKeys = WHEEL_FILTER_KEYS;
  if (formType === PRODUCT_TYPES.ACCESSORIES)
    selectedFilterKeys = ACCESSORY_FILTER_KEYS;

  const filterKeys = useMemo(
    () => Object.values(selectedFilterKeys),
    [selectedFilterKeys]
  );

  function getAllowedFilterKey(
    currentFilterKey,
    filterKeys,
    selectedFilterKeys
  ) {
    const currentFilterKeyIdx = filterKeys.findIndex(
      k => k === currentFilterKey
    );
    const prevFilterKey = filterKeys[currentFilterKeyIdx - 1];
    const afterFilterKey = filterKeys[currentFilterKeyIdx + 1];
    const initialFilterKeys = filterKeys.slice(0, 3);
    const unselectedInitialFilters = initialFilterKeys.filter(
      f => !selectedFilterKeys.includes(f)
    );

    let allowedFilterKey = prevFilterKey || afterFilterKey;

    if (unselectedInitialFilters.length === 1) {
      allowedFilterKey = unselectedInitialFilters[0];
    }

    return allowedFilterKey;
  }

  function getResetFilterOptions(filterOptions, prevFilterOptions) {
    if (Array.isArray(prevFilterOptions) && Array.isArray(filterOptions)) {
      return prevFilterOptions.map(prevFilterOption => {
        const initPrevFilterOption = {
          ...prevFilterOption,
          shouldResetFilter: true
        };

        const matchingNewFilterOption = filterOptions.find(
          o => o?.value === prevFilterOption?.value
        );

        const newFilterOption = matchingNewFilterOption
          ? { ...matchingNewFilterOption, shouldResetFilter: false }
          : {};

        const updatedFilterOption = {
          ...initPrevFilterOption,
          ...newFilterOption
        };

        return updatedFilterOption;
      });
    } else if (Array.isArray(filterOptions)) {
      return filterOptions;
    }

    return [];
  }

  const onSelectFilter = useCallback(
    (selectedFilter, shouldResetFilter) => {
      const selectedCanonicalFilters = selectedCanonicalFiltersRef.current;
      const selectedQueryFilters = selectedQueryFiltersRef.current;
      const nextFilters = nextFiltersRef.current;
      const selectedFilterIdx = findLastIndex(
        filterKeys,
        filterKey => filterKey in selectedFilter
      );

      let updatedSelectedQueryFilters = {};
      let updatedSelectedCanonicalFilters = { ...selectedCanonicalFilters };
      let wasAutoSelected = false;
      let selectedFilterKey = null;
      let hasPrevQueryFilterSelection = false;

      for (let key in selectedFilter) {
        selectedFilterKey = key;
        selectedFilter[key].shouldResetFilter = false;
        if (selectedFilter[key]?.wasAutoSelected) {
          wasAutoSelected = true;
        }

        if (selectedQueryFilters[key]) {
          hasPrevQueryFilterSelection = true;
        }
      }
      setShouldContinueAutoSelecting(!wasAutoSelected);

      if (shouldResetFilter || wasAutoSelected || hasPrevQueryFilterSelection) {
        updatedSelectedQueryFilters = {
          ...selectedFilter
        };

        if (wasAutoSelected) {
          const allowedSelectedQueryFilters = {};

          for (let key in selectedQueryFilters) {
            if (key in nextFilters) {
              if (
                nextFilters[key].find(
                  f => f.value === selectedQueryFilters[key].value
                )
              ) {
                allowedSelectedQueryFilters[key] = selectedQueryFilters[key];
              }
            } else {
              allowedSelectedQueryFilters[key] = selectedQueryFilters[key];
            }
          }

          updatedSelectedQueryFilters = {
            ...allowedSelectedQueryFilters,
            ...selectedFilter
          };
        }

        updatedSelectedCanonicalFilters = {
          ...selectedCanonicalFilters,
          ...updatedSelectedQueryFilters
        };
      } else {
        updatedSelectedCanonicalFilters = {
          ...selectedCanonicalFilters,
          ...selectedFilter
        };

        updatedSelectedQueryFilters = filterKeys.reduce(
          (acc, filterKey, idx) => {
            if (
              idx <= selectedFilterIdx &&
              updatedSelectedCanonicalFilters[filterKey]
            ) {
              acc[filterKey] = updatedSelectedCanonicalFilters[filterKey];
            }

            return acc;
          },
          { ...selectedQueryFilters }
        );
      }

      // TODO: Remove this reordering once graphQL api is fixed to not care about it
      updatedSelectedQueryFilters = filterKeys.reduce((acc, filterKey) => {
        if (updatedSelectedQueryFilters[filterKey]) {
          acc[filterKey] = updatedSelectedQueryFilters[filterKey];
        }
        return acc;
      }, {});

      setSelectedCanonicalFilters(updatedSelectedCanonicalFilters);
      setSelectedQueryFilters(updatedSelectedQueryFilters);

      lastSelectedFilterKeyRef.current = selectedFilterKey;
    },
    [filterKeys]
  );

  const updateCanonicalFilters = useCallback(
    nextFiltersData => {
      if (!Array.isArray(nextFiltersData)) {
        nextFiltersRef.current = null;
        return;
      }

      const nextFilters = getSortedNextFilters(nextFiltersData, filterKeys);
      const canonicalFilters = canonicalFiltersRef.current;
      const selectedCanonicalFilters = selectedCanonicalFiltersRef.current;
      const selectedQueryFilters = selectedQueryFiltersRef.current;
      const selectedQueryFilterKeys = Object.keys(selectedQueryFilters);
      const autoSelectFilters = {};
      const replaceFilters = {};
      const nextFilterKeys = [];

      nextFiltersRef.current = nextFilters;

      for (let i = 0; i < nextFilters.length; i++) {
        let nextFilter = nextFilters[i];
        const nextFilterKey = nextFilter?.key;
        let nextFilterOptions = [];

        if (Array.isArray(nextFilter?.options)) {
          nextFilterOptions = nextFilter.options
            .sort(sortFilterOptions)
            .map(o => ({
              label: o,
              value: o
            }));
        }

        if (nextFilterKey) {
          nextFilterKeys.push(nextFilterKey);
          replaceFilters[nextFilterKey] = nextFilterOptions;
        }

        nextFiltersRef.current = replaceFilters;

        if (nextFilterOptions.length === 1 && shouldContinueAutoSelecting) {
          autoSelectFilters[nextFilterKey] = {
            ...nextFilterOptions[0],
            wasAutoSelected: true
          };
        } else if (selectedCanonicalFilters[nextFilterKey]) {
          const prevSelectedFilter = selectedCanonicalFilters[nextFilterKey];
          const allowedSelectedFilterKey = getAllowedFilterKey(
            lastSelectedFilterKeyRef.current,
            filterKeys,
            selectedQueryFilterKeys
          );
          const autoSelectFilterCount = Object.keys(autoSelectFilters).length;

          if (
            formType === PRODUCT_TYPES.WHEELS &&
            nextFilter?.options?.includes(prevSelectedFilter.value) &&
            !prevSelectedFilter.wasAutoSelected &&
            autoSelectFilterCount === 0
          ) {
            autoSelectFilters[nextFilterKey] = {
              ...prevSelectedFilter,
              wasAutoSelected: true
            };
          } else if (
            nextFilter?.options.includes(prevSelectedFilter.value) &&
            !prevSelectedFilter.wasAutoSelected &&
            autoSelectFilterCount === 0 &&
            (nextFilterKey === allowedSelectedFilterKey ||
              (!selectedCanonicalFilters[lastSelectedFilterKeyRef.current]
                ?.shouldResetFilter &&
                nextFilterKey === allowedSelectedFilterKey))
          ) {
            autoSelectFilters[allowedSelectedFilterKey] = {
              ...selectedCanonicalFilters[allowedSelectedFilterKey],
              wasAutoSelected: true
            };
          }
        }
      }

      if (isCanonical) {
        const allowedFilters = {};
        const allowedSelectedFilters = {};
        const selectedQueryFilterKeys = Object.keys(selectedQueryFilters);
        const allowedFilterKeys = [
          ...selectedQueryFilterKeys,
          ...nextFilterKeys
        ];

        for (let key of allowedFilterKeys) {
          if (replaceFilters[key]) {
            replaceFilters[key] = getResetFilterOptions(
              replaceFilters[key],
              canonicalFilters[key]
            );
          }
        }

        const combinedFilters = {
          ...canonicalFilters,
          ...replaceFilters
        };

        for (let key in combinedFilters) {
          if (
            selectedCanonicalFilters[key] &&
            allowedFilterKeys.includes(key) &&
            !nextFilterKeys.includes(key)
          ) {
            allowedSelectedFilters[key] = selectedCanonicalFilters[key];
          }
          if (allowedFilterKeys.includes(key)) {
            allowedFilters[key] = combinedFilters[key];
          }
        }

        setCanonicalFilters(allowedFilters);
        setSelectedCanonicalFilters(allowedSelectedFilters);

        const autoSelectFilterKeys = Object.keys(autoSelectFilters);

        if (autoSelectFilterKeys.length > 0 && isCanonical) {
          const lastAutoSelectedFilterKey = autoSelectFilterKeys.slice(-1);
          lastSelectedFilterKeyRef.current = lastAutoSelectedFilterKey;

          onSelectFilter(autoSelectFilters);

          return;
        }
      }
    },
    [
      isCanonical,
      filterKeys,
      formType,
      onSelectFilter,
      shouldContinueAutoSelecting
    ]
  );

  function onComplete(completedFilters) {
    const lastSelectedFilterKey = lastSelectedFilterKeyRef.current;

    if (completedFilters) {
      const updatedCanonicalFilters = {};
      const updatedSelectedCanonicalFilters = {
        ...selectedCanonicalFilters,
        ...completedFilters
      };

      for (let key in canonicalFilters) {
        if (
          updatedSelectedCanonicalFilters[key] ||
          key !== lastSelectedFilterKey
        ) {
          let filterOptions = [updatedSelectedCanonicalFilters[key]];
          const prevFilterOptions = canonicalFilters[key];

          updatedCanonicalFilters[key] = getResetFilterOptions(
            filterOptions,
            prevFilterOptions
          );
        }
      }

      setSelectedCanonicalFilters(updatedSelectedCanonicalFilters);
      setCanonicalFilters(updatedCanonicalFilters);
    }
  }

  useEffect(() => {
    if (isCanonical && !isEqual(prevNextFilters, nextFilters)) {
      updateCanonicalFilters(nextFilters);
    }
  }, [
    isCanonical,
    nextFilters,
    prevNextFilters,
    productType,
    updateCanonicalFilters
  ]);

  return {
    filters: canonicalFilters,
    onComplete,
    onSelectFilter,
    queryFilters: selectedQueryFilters,
    selectedFilters: selectedCanonicalFilters
  };
}

export default useCanonicalForm;
